/**
 * OutboxHealthPill.jsx — live status pill for the email outbox queue.
 *
 * Polls /api/admin/outbox-stats every 30 s and shows a coloured badge:
 *   • green  — no pending, no failed → all quiet
 *   • amber  — pending > 0 && oldest < 5 min → draining normally
 *   • red    — failed > 0 OR oldest > 5 min → attention needed
 *
 * Clicking the pill routes to /admin/email-log filtered to the
 * offending status. Rendered in the header of AppLayout for any user
 * with at least client_admin role.
 *
 * Shipped 2026-07-14 as P3b of the post-R7 backlog.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useViewAsClient } from "@/lib/viewAsClient";
import { Mail } from "lucide-react";
import { authHeaders } from "@/lib/api/_shared";
import { useAuth } from "@/lib/auth";

const POLL_MS = 30_000;
const STALE_PENDING_SECONDS = 300;   // 5 min → red

/** Bucket the API response into a UI state. */
function bucketise(stats) {
  if (!stats) return { tone: "gray", label: "Outbox: checking…", href: "/admin/email-log" };
  if (stats.unavailable) return { tone: "gray", label: "Outbox: unavailable", href: "/admin/email-log" };
  if (stats.checked_at === null) {
    return { tone: "gray", label: "Outbox: not configured", href: "/admin/email-log" };
  }
  const pending = stats.pending || 0;
  const failed  = stats.failed  || 0;
  const stale   = (stats.oldest_pending_seconds || 0) > STALE_PENDING_SECONDS;

  if (failed > 0) {
    return {
      tone: "red",
      label: `Outbox: ${failed} failed`,
      href: "/admin/email-log?status=failed",
    };
  }
  if (stale) {
    return {
      tone: "red",
      label: `Outbox: stuck (${Math.floor(stats.oldest_pending_seconds / 60)} min)`,
      href: "/admin/email-log?status=pending",
    };
  }
  if (pending > 0) {
    return {
      tone: "amber",
      label: `Outbox: ${pending} pending`,
      href: "/admin/email-log?status=pending",
    };
  }
  return {
    tone: "green",
    label: "Outbox: healthy",
    href: "/admin/email-log",
  };
}

const TONE_CLASS = {
  green: "bg-emerald-100 text-emerald-700 hover:bg-emerald-200",
  amber: "bg-amber-100  text-amber-800  hover:bg-amber-200",
  red:   "bg-red-100    text-red-800    hover:bg-red-200",
  gray:  "bg-slate-100  text-slate-600  hover:bg-slate-200",
};

export default function OutboxHealthPill() {
  const navigate = useNavigate();
  const { viewAsClientId: viewAs } = useViewAsClient() || {};
  const { assignments = [], hasRole } = useAuth();
  // Non-super admins are always scoped to their own tenant.
  const viewAsClientId = hasRole(["super_admin"]) ? viewAs : assignments.find((a) => a.client_id)?.client_id || "";
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const url = new URL("/api/admin/outbox-stats", process.env.REACT_APP_BACKEND_URL);
      if (viewAsClientId) url.searchParams.set("client_id", viewAsClientId);
      const res = await fetch(url.toString(), { headers: await authHeaders() });
      setStats(res.ok ? await res.json() : (prev) => prev || { unavailable: true });
    } catch {
      setStats((prev) => prev || { unavailable: true });
    }
  }, [viewAsClientId]);

  useEffect(() => {
    fetchStats();
    const id = setInterval(fetchStats, POLL_MS);
    return () => clearInterval(id);
  }, [fetchStats]);
  const { tone, label, href } = bucketise(stats);
  const cls = TONE_CLASS[tone];

  return (
    <button
      type="button"
      data-testid="outbox-health-pill"
      data-tone={tone}
      onClick={() => navigate(href)}
      className={`hidden md:inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${cls}`}
      title={
        stats
          ? `pending: ${stats.pending || 0} • failed: ${stats.failed || 0} • skipped: ${stats.skipped || 0} • sent (24h): ${stats.sent_24h || 0}`
          : "Loading outbox status…"
      }
    >
      <Mail className="w-3 h-3" />
      {label}
    </button>
  );
}
